import { cn } from '@gto/ui/cn'

/**
 * Classes that an ancestor's `:has()` tests to follow a component's state.
 *
 * Chrome restyles the whole document on every DOM insertion while any loaded `:has()`
 * argument holds an attribute selector (`:has([data-popup-open])`), a `:not()` or a bare `*`,
 * whether or not anything on the page matches it: 30 to 150 ms per insertion on the study
 * pages. A class or a pseudo-class argument costs nothing, so a component whose state an
 * ancestor reads sets one of these classes beside its data attribute. Readers spell the class
 * literally (`group-has-[.ui-invalid]/field:`), because Tailwind reads class names from source.
 */

/** On an input group whose control is invalid. `InputGroup` sets it. */
export const INVALID_MARKER = 'ui-invalid'

/** On a popup trigger while its popup is open (Base UI's `data-popup-open`). */
export const POPUP_OPEN_MARKER = 'ui-popup-open'

type StateClassName<State> = string | ((state: State) => string | undefined) | undefined

/**
 * A Base UI trigger `className` that adds `POPUP_OPEN_MARKER` while the popup is open, after the
 * given classes (a caller's state function is resolved first). Every DS popup trigger uses it.
 */
export const withPopupOpenMarker =
  <State extends { open: boolean }>(...classNames: StateClassName<State>[]) =>
  (state: State) =>
    cn(
      classNames.map((className) =>
        typeof className === 'function' ? className(state) : className,
      ),
      state.open && POPUP_OPEN_MARKER,
    )

/** On a DataTable's end-overflow fade while it is shown. */
export const END_SHADOW_SHOWN_MARKER = 'ui-end-shadow-shown'
